import { getEventById, getEventsForDate } from '../store/events';

// A day shows its events in a grid two cells wide and two high: a 50×50 event takes one cell, a 100×50 or
// 50×100 one takes two, and a 100×100 one all four. The API refuses an event its day has no room for, and
// when the free cells are scattered it moves the day's events to gather them. Days that already hold more
// keep their events, but take no more.
const span = (size) => Math.min(Math.max(Math.floor(size / 50), 1), 2);
const cellsOf = (event) => span(event.width) * span(event.height);
const isWide = (event) => span(event.width) === 2 && span(event.height) === 1;
const isTall = (event) => span(event.width) === 1 && span(event.height) === 2;

export const SMALL_EVENT = { width: 50, height: 50 };

// Whether events fit in one day: four cells at most, and never a wide one with a tall one, as the wide one
// takes a whole row and the tall one a whole column.
export const fitsInDay = (events) =>
    events.reduce((cells, event) => cells + cellsOf(event), 0) <= 4 && !(events.some(isWide) && events.some(isTall));

// Whether event can come to a day holding dayEvents.
export const hasRoomFor = (dayEvents, event) => fitsInDay([...dayEvents.filter((e) => e.id !== event.id), event]);

// Whether an event can take a new size on its day. The day must still fit, though on a day that was already
// over, an event may keep or lower its size.
export const canResize = (dayEvents, event, size) => {
    const others = dayEvents.filter((e) => e.id !== event.id);
    return fitsInDay([...others, { ...event, ...size }])
        || (!fitsInDay([...others, event]) && cellsOf(size) <= cellsOf(event));
};

export const isDayFull = (dayEvents) => !hasRoomFor(dayEvents, SMALL_EVENT);

// The places an event can take in its day, size by size: a quarter, half a day across or down, or all of it.
export const LAYOUTS = [
    { key: 'quarter', label: '1/4', width: 50, height: 50, spots: [[0, 0], [1, 0], [0, 1], [1, 1]] },
    { key: 'wide', label: '1/2 по горизонтали', width: 100, height: 50, spots: [[0, 0], [0, 1]] },
    { key: 'tall', label: '1/2 по вертикали', width: 50, height: 100, spots: [[0, 0], [1, 0]] },
    { key: 'full', label: '1/1', width: 100, height: 100, spots: [[0, 0]] },
];

// The cells ("x,y") an event takes where it stands, kept inside the columns as the calendar draws it.
const cellsAt = (event) => {
    const w = span(event.width);
    const h = span(event.height);
    const x = Math.min(Math.max(event.pos_x ?? 0, 0), 2 - w);
    const y = Math.max(event.pos_y ?? 0, 0);
    const cells = [];
    for (let cy = y; cy < y + h; cy++) {
        for (let cx = x; cx < x + w; cx++) cells.push(`${cx},${cy}`);
    }
    return cells;
};

const samePlace = (a, b) => cellsAt(a).join() === cellsAt(b).join();

// Whether event (null for a new one) can take place ({ width, height, pos_x, pos_y }) on a day holding
// dayEvents without moving them: the day must have room for that size, and no other event may stand there.
// An event can always stay as it is on its own day.
export const canPlace = (dayEvents, event, place) => {
    const onItsDay = event != null && dayEvents.some((e) => e.id === event.id);
    if (onItsDay && samePlace(event, place)) return true;

    const size = { width: place.width, height: place.height };
    if (!(onItsDay ? canResize(dayEvents, event, size) : hasRoomFor(dayEvents, size))) return false;

    const taken = new Set(dayEvents.filter((e) => e.id !== event?.id).flatMap(cellsAt));
    return cellsAt(place).every((cell) => !taken.has(cell));
};

// The first place, row by row, where a small event can go on the day, or null when there's none.
export const firstFreePlace = (dayEvents, event) => {
    const { width, height, spots } = LAYOUTS[0];
    const places = spots.map(([pos_x, pos_y]) => ({ width, height, pos_x, pos_y }));
    return places.find((place) => canPlace(dayEvents, event, place)) ?? null;
};

export const DAY_FULL_MESSAGE = 'В этом дне больше нет места для записей';
export const NO_ROOM_MESSAGE = 'В этом дне нет места для записи такого размера';

// What to say when an event can't come to a day holding dayEvents.
export const noRoomMessage = (dayEvents) => (isDayFull(dayEvents) ? DAY_FULL_MESSAGE : NO_ROOM_MESSAGE);

// What the API answers when the day had no room, e.g. after events were added in another tab.
export const isDayFullError = (error) => error?.response?.status === 409;

// The events on a day ('YYYY-MM-DD'), from the store.
export const eventsOnDay = (state, day) => getEventsForDate(state, day).map((id) => getEventById(state, id)).filter(Boolean);

// The day's events the API moved aside to make room for the one it was sent, as updateEventsPositions takes them.
export const rearranged = (res) => res?.data?.rearranged ?? [];
